import { Injectable } from '@angular/core';
import { backgroundCssStyle } from '../../../../shared/utils/css-color.util';

export interface RiskRegisterMatrixCell {
    agentId: string;
    agentName: string;
    consequenceRating: string | null;
    color: string;
    [key: string]: unknown;
}

export interface RiskRegisterMatrixGroup {
    key: string;
    label: string;
}

export interface RiskRegisterMatrixRow {
    segId: string;
    segCode: string | null;
    segName: string;
    numberOfWorkers: number | null;
    numberOfContractors: number | null;
    cells: RiskRegisterMatrixCell[];
    group: RiskRegisterMatrixGroup | null;
}

export interface RiskRegisterMatrixColumn {
    agentId: string;
    agentName: string;
    consequenceRating: string | null;
}

export interface RiskRegisterStatistic {
    key: string;
    value: string;
    tableKey: string;
}

export interface RiskRegisterRiskLevel {
    color: string;
}

export interface RiskRegisterMatrixData {
    rows: RiskRegisterMatrixRow[];
    columns: RiskRegisterMatrixColumn[];
    statistics: RiskRegisterStatistic[];
    riskLevels: RiskRegisterRiskLevel[];
}

export interface RiskRegisterPresentationLabels {
    segCode: string;
    segName: string;
    workers: string;
    contractors: string;
    ratingPrefix: string;
    consequenceRating: string;
    footer: string;
}

export interface RiskRegisterColumnView {
    agentName: string;
    consequenceRating: string;
}

export interface RiskRegisterCellView {
    cellStyle: string;
    statText: string;
}

export interface RiskRegisterRowView {
    segCode: string;
    segName: string;
    numberOfWorkers: number | string;
    numberOfContractors: number | string;
    cells: RiskRegisterCellView[];
    segNameStyle: string;
}

export interface RiskRegisterChunkView {
    columns: RiskRegisterColumnView[];
    rows: RiskRegisterRowView[];
}

export interface RiskRegisterLegendItemView {
    swatchStyle: string;
    label: string;
}

export interface RiskRegisterMatrixPresentation {
    chunks: RiskRegisterChunkView[];
    legendItems: RiskRegisterLegendItemView[];
    segCodeLabel: string;
    segNameLabel: string;
    workersLabel: string;
    contractorsLabel: string;
    consequenceRatingLabel: string;
    footerLabel: string;
    hasData: boolean;
}

const AGENTS_PER_TABLE = 4;

const RR_CELL_BASE = 'border:1px solid #ccc;padding:6px 8px;font-size:10px;';

export const RR_STYLES = {
    table: 'width:100%;border-collapse:collapse;font-size:11px;font-family:Arial,sans-serif;color:#000;',
    chunk: 'margin-bottom:16px;page-break-inside:avoid;break-inside:avoid;',
    headerCell: `${RR_CELL_BASE}background:#eeeeee !important;font-weight:bold;text-align:center;vertical-align:middle;`,
    consequence: 'font-size:10px;font-weight:normal;color:#444;',
    fixedCell: `${RR_CELL_BASE}vertical-align:top;white-space:nowrap;`,
    groupHeaderText: 'font-weight:bold;',
    matrixCell: `${RR_CELL_BASE}vertical-align:top;text-align:left;white-space:pre-line;background:rgba(55,55,55,0.4) !important;`,
    legend: 'margin-top:20px;display:flex;flex-wrap:wrap;gap:16px;align-items:center;',
    legendItem: 'display:inline-flex;align-items:center;gap:4px;font-size:11px;',
    swatch: 'display:inline-block;width:14px;height:14px;border:1px solid rgba(0,0,0,0.2);',
    footer: 'margin-top:8px;font-weight:bold;font-size:11px;'
} as const;

@Injectable({ providedIn: 'root' })
export class RiskRegisterTablePresentationService {
    build(
        data: RiskRegisterMatrixData | null | undefined,
        labels: RiskRegisterPresentationLabels
    ): RiskRegisterMatrixPresentation {
        const empty: RiskRegisterMatrixPresentation = {
            chunks: [],
            legendItems: [],
            segCodeLabel: labels.segCode,
            segNameLabel: labels.segName,
            workersLabel: labels.workers,
            contractorsLabel: labels.contractors,
            consequenceRatingLabel: labels.consequenceRating,
            footerLabel: labels.footer,
            hasData: false
        };

        if (!data?.rows?.length || !data.columns.length) {
            return empty;
        }

        const chunks: RiskRegisterChunkView[] = [];
        for (let i = 0; i < data.columns.length; i += AGENTS_PER_TABLE) {
            const chunkColumns = data.columns.slice(i, i + AGENTS_PER_TABLE);
            chunks.push({
                columns: chunkColumns.map(col => ({
                    agentName: col.agentName,
                    consequenceRating: col.consequenceRating ?? '-'
                })),
                rows: data.rows.flatMap((row, index) => {
                    const view: RiskRegisterRowView = {
                        segCode: row.segCode ?? '-',
                        segName: row.segName,
                        numberOfWorkers: row.numberOfWorkers ?? '-',
                        numberOfContractors: row.numberOfContractors ?? '-',
                        cells: chunkColumns.map(col => this.buildCell(row, col, data.statistics)),
                        segNameStyle: RR_STYLES.fixedCell
                    };
                    const group = row.group;
                    return group && group.key !== data.rows[index - 1]?.group?.key
                        ? [this.buildGroupHeader(group, chunkColumns.length), view]
                        : [view];
                })
            });
        }

        const legendItems: RiskRegisterLegendItemView[] = data.riskLevels.map((level, index) => ({
            swatchStyle: backgroundCssStyle(level.color),
            label: `${labels.ratingPrefix} ${index}`
        }));

        return {
            chunks,
            legendItems,
            segCodeLabel: labels.segCode,
            segNameLabel: labels.segName,
            workersLabel: labels.workers,
            contractorsLabel: labels.contractors,
            consequenceRatingLabel: labels.consequenceRating,
            footerLabel: labels.footer,
            hasData: true
        };
    }

    private buildGroupHeader(group: RiskRegisterMatrixGroup, columnCount: number): RiskRegisterRowView {
        return {
            segCode: '',
            segName: group.label,
            numberOfWorkers: '',
            numberOfContractors: '',
            cells: Array.from({ length: columnCount }, () => ({ cellStyle: '', statText: '' })),
            segNameStyle: RR_STYLES.fixedCell + RR_STYLES.groupHeaderText
        };
    }

    private buildCell(
        row: RiskRegisterMatrixRow,
        column: RiskRegisterMatrixColumn,
        statistics: RiskRegisterStatistic[]
    ): RiskRegisterCellView {
        const cell = row.cells.find(c => c.agentId === column.agentId);
        if (!cell) return { cellStyle: '', statText: '' };
        return {
            cellStyle: backgroundCssStyle(cell.color),
            statText: statistics.map(stat => `${stat.tableKey} = ${String(cell[stat.key] ?? 0)}`).join('\n')
        };
    }

}
